import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Button, Dropdown, Input, MultiInput } from 'sfy-react'

import { postRequestAsync } from '../../../common/genericApi'
import { setLoader } from '../../../store/common'
import { checkValues } from '../../../utils/regex'
import EditIcon from '../../../assets/images/edit.svg'

const COUNTRY_OPTIONS = [
  {
    key: 'in',
    value: 'India',
    uriKey: 'uriIN',
  },
  {
    key: 'us',
    value: 'US',
    uriKey: 'uriUS',
  },
  {
    key: 'eu',
    value: 'Europe',
    uriKey: 'uriEU',
  },
  {
    key: 'croma',
    value: 'Croma',
    uriKey: 'uriCroma',
  },
]

const servifyEmailRegEx = /^[a-zA-Z0-9._%+-]+@(?:servify\.tech|servify\.in|servify\.com)$/

const SignUp = ({ onStepChange }) => {
  const dispatch = useDispatch()
  let emailValidationTimeout = null
  const [step, setStep] = useState(1)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [formSuccessMessage, setFormMsg] = useState()
  const [formError, setFormError] = useState('')
  const [selectedRegion, setSelectedRegion] = useState({})
  const [countryOptions, setCountryOptions] = useState(COUNTRY_OPTIONS)
  const [email, setEmail] = useState({
    value: '',
    placeholder: 'Enter your email',
    error: '',
  })
  const [otp, setOtp] = useState(null)
  const [timer, setTimer] = useState(60)
  const [isTimerRunning, setIsTimerRunning] = useState(false)

  useEffect(() => {
    if (onStepChange) {
      onStepChange(step)
    }
  }, [step, onStepChange])

  useEffect(() => {
    let interval
    if (isTimerRunning) {
      interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(interval)
            setIsTimerRunning(false)
            return 0
          }
          return prevTimer - 1
        })
      }, 1000)
    }

    return () => clearInterval(interval)
  }, [isTimerRunning])

  useEffect(() => {
    let { isValid, isEmpty } = checkValues({ email: email.value })
    if (isEmpty) return
    if (isValid) setEmail((prevState) => ({ ...prevState, error: '' }))
    else if (isEmpty) setEmail((prevState) => ({ ...prevState, error: 'Please enter email address' }))
  }, [isSubmitted, email.value])

  useEffect(() => {
    const filteredOptions = COUNTRY_OPTIONS.filter((eachCountry) => window[eachCountry['uriKey']])
    setCountryOptions(COUNTRY_OPTIONS.filter((eachCountry) => window[eachCountry['uriKey']]))

    if (localStorage.getItem('selectedRegion')) {
      const selectedRegionOption = filteredOptions.filter((opt) => opt.key === localStorage.getItem('selectedRegion'))
      handleCountrySelection(selectedRegionOption[0])
    } else {
      handleCountrySelection(filteredOptions[0])
    }
  }, [])

  const handleCountrySelection = (selectedOption) => {
    localStorage.setItem('selectedRegion', selectedOption.key)
    setSelectedRegion(selectedOption)
  }

  const handleOnMultiOTPChange = (val) => {
    setOtp(val)
    setFormError('')
  }

  const startTimer = () => {
    setIsTimerRunning(true)
    setTimer(60)
  }

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  const handleResendOTP = async () => {
    if (isTimerRunning) return
    startTimer()
    try {
      setFormMsg('')
      setFormError('')
      dispatch(setLoader(true))
      const response = await postRequestAsync('/otp/requestOTP', {
        emailID: email.value,
        isSignUp: true,
      })
      if (response.success) setFormMsg('OTP sent successfully.')
      dispatch(setLoader(false))
    } catch (e) {
      console.log(e)
      setFormError(e.msg || 'Could not send OTP.')
      dispatch(setLoader(false))
    }
  }

  const handleSendVerificationCode = async (e) => {
    e.preventDefault()
    setIsSubmitted(true)
    if (!email.error && email.value) {
      try {
        dispatch(setLoader(true))
        const response = await postRequestAsync('/otp/requestOTP', {
          emailID: email.value,
          isSignUp: true,
        })
        if (response.success) {
          setFormMsg('OTP sent successfully.')
          setStep(2)
          startTimer()
        }
        dispatch(setLoader(false))
      } catch (e) {
        console.log(e)
        setEmail({ ...email, error: e.msg || 'Could not send OTP.' })
        dispatch(setLoader(false))
      }
    }
  }

  const handleVerifyCode = async (e) => {
    e.preventDefault()
    setIsSubmitted(true)
    if (!otp || otp.length !== 6) {
      setFormError('Please enter the verification code')
      return
    }
    try {
      setFormError('')
      dispatch(setLoader(true))
      const response = await postRequestAsync('/otp/verifyOTP', {
        emailID: email.value,
        OTP: otp,
        isSignUp: true,
      })
      if (response.success) {
        const id = response?.data?.accessToken?.id
        console.log('id', id)
        localStorage.removeItem('isLoggedIn')
        localStorage.setItem('emailID', email.value)
        localStorage.setItem('isSignUp', true)
        localStorage.setItem('id', id)
        window.open('/user/central/raiseNewRequest', '_self')
      }
      dispatch(setLoader(false))
    } catch (e) {
      console.log(e)
      setFormError(e.msg || 'Could not validate OTP.')
      dispatch(setLoader(false))
    }
  }

  const handleOnChange = (e) => {
    const newEmailValue = e.target.value.trim()
    setIsSubmitted(false)
    setEmail({
      ...email,
      value: newEmailValue,
    })

    clearTimeout(emailValidationTimeout)

    emailValidationTimeout = setTimeout(() => {
      if (!servifyEmailRegEx.test(newEmailValue)) {
        setEmail((prevState) => ({
          ...prevState,
          error: 'Please enter a valid servify email address',
        }))
      } else {
        setEmail((prevState) => ({
          ...prevState,
          error: '',
        }))
      }
    }, 700)
  }

  const handleEditEmail = () => {
    setStep(1)
    setOtp(null)
    setFormMsg('')
    setFormError('')
    setIsTimerRunning(false)
    setTimer(60)
  }

  // Step 1: Email Entry
  if (step === 1) {
    return (
      <>
        <div className='label mb5'>Your Region</div>
        <Dropdown
          classObject={{ container: `small-dropdown country-dropdown` }}
          options={countryOptions}
          value={selectedRegion?.value}
          handleOnChange={(value) => {
            handleCountrySelection(value)
          }}
          filterBy='value'
          searchText='Select'
          isDisabled={false}
        />
        <form onSubmit={handleSendVerificationCode}>
          <Input
            label='Email Address'
            placeholder={email.placeholder}
            message={{ value: email.error }}
            className={{ message: email.error ? 'error' : '', label: 'label' }}
            value={email.value}
            handleOnChange={handleOnChange}
            name={'email'}
            otherProps={{ autoFocus: true }}
          />
          {formSuccessMessage && (
            <div className='d-flex'>
              <div className='success color-theme mt10'>{formSuccessMessage}</div>
            </div>
          )}
          <Button
            type='submit'
            text='Send Verification Code'
            classes='button-primary button-submit mt30'
            isDisabled={!email.value || email.error}
          />
        </form>
      </>
    )
  }

  // Step 2: Email Verification
  return (
    <div className='sign-up-verification'>
      <div className='sign-up-verification__header'>Verify your email</div>

      <div className='sign-up-verification__email mb4'>
        <div>
          <div className='sign-up-verification__instruction mb0'>Enter verification code sent to</div>
          <span>{email.value}</span>
        </div>
        <button type='button' className='sign-up-verification__email-edit' onClick={handleEditEmail} aria-label='Edit email'>
          <img src={EditIcon} alt='Edit email' />
        </button>
      </div>
      <form onSubmit={handleVerifyCode}>
        <div className='sign-up-verification__otp-container mb24'>
          <MultiInput classNames={{ input: 'custom-multi-input-class' }} handleOnChange={handleOnMultiOTPChange} length={6} />
        </div>
        {formError && (
          <div className='d-flex mb20'>
            <div className='error'>{formError}</div>
          </div>
        )}
        <Button
          type='submit'
          text='Verify Code'
          classes='button-primary sign-up-verification__verify-button mb18'
          isDisabled={!otp || otp.length !== 6}
        />
        <div className='sign-up-verification__resend'>
          <span className='sign-up-verification__resend-text'>Haven't received code?</span>
          <button type='button' className='sign-up-verification__resend-link' onClick={handleResendOTP} disabled={isTimerRunning}>
            {isTimerRunning ? (
              <>
                <span className='sign-up-verification__resend-link-text'>Resend Code in</span>
                <span className='sign-up-verification__resend-link-timer'>{formatTimer(timer)}</span>
              </>
            ) : (
              'Resend Code'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default SignUp
